/**
 * Bridge Game Component
 * A classic trick-taking card game for 4 players
 * Features: Card sorting, bid history tooltip, play phase tutorial, Coach mode
 */

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, RotateCcw, HelpCircle, Trophy, Bot, Wifi, ArrowUpDown, Info, X, Lightbulb, GraduationCap, TrendingUp, Eye, EyeOff, Pause, Play, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import useBridgeStore from './bridgeStore';
import BridgeMultiplayer from './BridgeMultiplayer';
import {
  SUITS,
  POSITIONS,
  BID_LEVELS,
  BID_STRAINS,
  SPECIAL_BIDS,
  SUIT_ORDER
} from './bridgeConstants';
import {
  calculateHCP,
  calculateDistributionPoints,
  analyzeSuits,
  getBiddingSuggestion,
  getPlaySuggestion,
  calculateContractOdds,
  getBridgeLearningExplanation,
  getBridgeContextualTips
} from './bridgeCoach';

// Strain symbols mapping
const STRAIN_SYMBOLS = {
  CLUBS: '♣',
  DIAMONDS: '♦',
  HEARTS: '♥',
  SPADES: '♠',
  NOTRUMP: 'NT'
};

const STRAIN_COLORS = {
  CLUBS: '#1e293b',
  DIAMONDS: '#dc2626',
  HEARTS: '#dc2626',
  SPADES: '#1e293b',
  NOTRUMP: '#059669'
};

// Sort hand by suit (Spades, Hearts, Diamonds, Clubs) then by rank within each suit
function sortBySuit(cards) {
  return [...cards].sort((a, b) => {
    const suitDiff = SUITS[b.suit].rank - SUITS[a.suit].rank;
    if (suitDiff !== 0) return suitDiff;
    return b.value - a.value;
  });
}

// Sort hand by rank (Aces first, then Kings, etc) across all suits
function sortByRank(cards) {
  return [...cards].sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value;
    return SUITS[b.suit].rank - SUITS[a.suit].rank;
  });
}

// Boosty Games Logo URL
const BOOSTY_LOGO_URL = "https://customer-assets.emergentagent.com/job_4e3de3c9-461d-4f73-ada0-30b07137d950/artifacts/0wd7buyq_Boosty%20Games%20v2.png";

// Sound Effects Manager
const SoundEffects = {
  audioContext: null,
  
  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.audioContext;
  },
  
  // Card shuffle sound - quick rustling
  playShuffle() {
    try {
      const ctx = this.init();
      const duration = 0.15;
      
      for (let i = 0; i < 5; i++) {
        const noise = ctx.createBufferSource();
        const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        
        for (let j = 0; j < buffer.length; j++) {
          data[j] = (Math.random() * 2 - 1) * 0.3;
        }
        
        noise.buffer = buffer;
        const filter = ctx.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.value = 2000;
        
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.1, ctx.currentTime + i * 0.08);
        gain.gain.exponentialDecayTo && gain.gain.exponentialDecayTo(0.01, ctx.currentTime + i * 0.08 + duration);
        
        noise.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);
        noise.start(ctx.currentTime + i * 0.08);
      }
    } catch (e) { /* Audio not supported */ }
  },
  
  // Card deal sound - soft thwip
  playDeal() {
    try {
      const ctx = this.init();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(800, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.05);
      
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.1);
    } catch (e) { /* Audio not supported */ }
  },
  
  // Card play sound - satisfying snap
  playCard() {
    try {
      const ctx = this.init();
      
      // Click component
      const click = ctx.createOscillator();
      const clickGain = ctx.createGain();
      click.type = 'square';
      click.frequency.setValueAtTime(1200, ctx.currentTime);
      click.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.02);
      clickGain.gain.setValueAtTime(0.15, ctx.currentTime);
      clickGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
      click.connect(clickGain);
      clickGain.connect(ctx.destination);
      click.start(ctx.currentTime);
      click.stop(ctx.currentTime + 0.06);
      
      // Thump component
      const thump = ctx.createOscillator();
      const thumpGain = ctx.createGain();
      thump.type = 'sine';
      thump.frequency.setValueAtTime(150, ctx.currentTime);
      thump.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.1);
      thumpGain.gain.setValueAtTime(0.2, ctx.currentTime);
      thumpGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
      thump.connect(thumpGain);
      thumpGain.connect(ctx.destination);
      thump.start(ctx.currentTime);
      thump.stop(ctx.currentTime + 0.15);
    } catch (e) { /* Audio not supported */ }
  },
  
  // Trick win sound - triumphant chime
  playWin() {
    try {
      const ctx = this.init();
      const notes = [523, 659, 784]; // C5, E5, G5 chord
      
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime);
        
        gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.05);
        gain.gain.linearRampToValueAtTime(0.12, ctx.currentTime + i * 0.05 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.05 + 0.4);
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + i * 0.05);
        osc.stop(ctx.currentTime + i * 0.05 + 0.5);
      });
    } catch (e) { /* Audio not supported */ }
  }
};

// Professional Card Component - Seamless logo-integrated design
const Card = ({ card, onClick, disabled, selected, small, faceDown, position, dealDelay = 0, playSound = false }) => {
  // Play deal sound on mount if requested
  React.useEffect(() => {
    if (playSound && dealDelay > 0) {
      const timer = setTimeout(() => SoundEffects.playDeal(), dealDelay * 1000);
      return () => clearTimeout(timer);
    }
  }, [playSound, dealDelay]);

  if (faceDown) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: -50, rotateY: 180 }}
        animate={{ opacity: 1, y: 0, rotateY: 0 }}
        transition={{ duration: 0.4, delay: dealDelay, type: 'spring', damping: 15 }}
        className={`
          ${small ? 'w-16 h-24' : 'w-20 h-28'} 
          rounded-xl relative overflow-hidden
        `}
        style={{
          // Match the logo's purple gradient background exactly
          background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 25%, #4c1d95 50%, #2e1065 75%, #1e1b4b 100%)',
          border: '2px solid #a855f7',
          boxShadow: '0 4px 16px rgba(139,92,246,0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
        }}
      >
        {/* Inner glow to match logo aesthetic */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 30%, rgba(249,115,22,0.15) 0%, transparent 50%)'
        }} />
        
        {/* Decorative lines matching logo style */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />
        </div>
        
        {/* Actual Boosty Games Logo - centered and seamless */}
        <div className="absolute inset-0 flex items-center justify-center">
          <img 
            src={BOOSTY_LOGO_URL} 
            alt="Boosty" 
            className={`${small ? 'h-14' : 'h-18'} w-auto object-contain`}
            style={{ 
              filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.4))',
              maxWidth: '90%'
            }}
          />
        </div>
        
        {/* Subtle corner embellishments */}
        <div className="absolute top-1 left-1 w-2 h-2 border-t border-l border-orange-400/50 rounded-tl" />
        <div className="absolute top-1 right-1 w-2 h-2 border-t border-r border-orange-400/50 rounded-tr" />
        <div className="absolute bottom-1 left-1 w-2 h-2 border-b border-l border-orange-400/50 rounded-bl" />
        <div className="absolute bottom-1 right-1 w-2 h-2 border-b border-r border-orange-400/50 rounded-br" />
      </motion.div>
    );
  }
  
  const isRed = card.suit === 'HEARTS' || card.suit === 'DIAMONDS';
  const suitColor = isRed ? '#dc2626' : '#1e293b';
  const suitSymbol = card.symbol;
  
  const handleClick = () => {
    if (!disabled && onClick) {
      SoundEffects.playCard();
      onClick(card);
    }
  };
  
  return (
    <motion.button
      data-testid={`card-${position || 'unknown'}-${card.id}`}
      initial={{ opacity: 0, y: -50, rotateY: 180 }}
      animate={{ opacity: 1, y: 0, rotateY: 0 }}
      transition={{ duration: 0.4, delay: dealDelay, type: 'spring', damping: 15 }}
      whileHover={!disabled ? { y: -12, scale: 1.02, zIndex: 100 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
      onClick={handleClick}
      disabled={disabled}
      className={`
        ${small ? 'w-16 h-24' : 'w-20 h-28'}
        rounded-xl relative
        ${selected ? 'ring-4 ring-amber-400 ring-offset-2 ring-offset-emerald-900 -translate-y-4' : ''}
        ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
        transition-all duration-150
      `}
      style={{
        background: '#ffffff',
        border: selected ? '3px solid #fbbf24' : '2px solid #cbd5e1',
        boxShadow: selected 
          ? '0 8px 20px rgba(251,191,36,0.4), 0 4px 8px rgba(0,0,0,0.3)' 
          : '0 2px 8px rgba(0,0,0,0.2)'
      }}
    >
      {/* Top left corner - rank and suit stacked */}
      <div className="absolute top-1 left-1.5 flex flex-col items-center leading-none">
        <span 
          className={`${small ? 'text-base' : 'text-lg'} font-black`} 
          style={{ color: suitColor }}
        >
          {card.display}
        </span>
        <span 
          className={`${small ? 'text-sm' : 'text-base'} -mt-0.5`} 
          style={{ color: suitColor }}
        >
          {suitSymbol}
        </span>
      </div>
      
      {/* Bottom right corner (rotated) - rank and suit stacked */}
      <div className="absolute bottom-1 right-1.5 flex flex-col items-center leading-none rotate-180">
        <span 
          className={`${small ? 'text-base' : 'text-lg'} font-black`} 
          style={{ color: suitColor }}
        >
          {card.display}
        </span>
        <span 
          className={`${small ? 'text-sm' : 'text-base'} -mt-0.5`} 
          style={{ color: suitColor }}
        >
          {suitSymbol}
        </span>
      </div>
    </motion.button>
  );
};

// Hand Component with proper card overlap and deal animations
const Hand = ({ cards, position, isCurrentPlayer, isDummy, showCards, onCardClick, currentTrick, playLock, sortMode }) => {
  const isHorizontal = position === 'NORTH' || position === 'SOUTH';
  const isPlayer = position === 'SOUTH';
  
  // Sort cards based on mode (only for player's visible hand)
  const sortedCards = (isPlayer || showCards) 
    ? (sortMode === 'rank' ? sortByRank(cards) : sortBySuit(cards))
    : cards;
  
  // Check which cards are legal to play
  const getLegalCards = () => {
    if (currentTrick.length === 0) return sortedCards.map(c => c.id);
    
    const leadSuit = currentTrick[0].card.suit;
    const suitCards = sortedCards.filter(c => c.suit === leadSuit);
    
    if (suitCards.length > 0) {
      return suitCards.map(c => c.id);
    }
    return sortedCards.map(c => c.id);
  };
  
  const legalCardIds = isCurrentPlayer ? getLegalCards() : [];
  
  // Calculate overlap - show enough of each card's corner for clear identification
  const getOverlap = () => {
    if (isPlayer) {
      return isHorizontal ? '-ml-[52px]' : '-mt-[72px]';
    }
    if (showCards) {
      // Dummy hand visible - reduce overlap to show rank AND suit clearly
      return isHorizontal ? '-ml-[48px]' : '-mt-[56px]';
    }
    return isHorizontal ? '-ml-[52px]' : '-mt-[76px]';
  };
  
  const overlap = getOverlap();
  
  // Calculate deal delay based on position (cards dealt clockwise from South)
  const positionDelayBase = {
    SOUTH: 0,
    WEST: 0.3,
    NORTH: 0.6,
    EAST: 0.9
  };
  
  return (
    <div className="flex flex-col items-center">
      <div className={`mb-2 px-3 py-1 rounded-full ${isCurrentPlayer ? 'bg-amber-500/20 border border-amber-500/40' : 'bg-slate-800/60'}`}>
        <span className={`text-sm font-bold uppercase tracking-wide ${isCurrentPlayer ? 'text-amber-400' : 'text-slate-400'}`}>
          {position} {isDummy && <span className="text-cyan-400 ml-1">(Dummy)</span>}
          {isCurrentPlayer && <span className="ml-2 text-green-400 animate-pulse">PLAY</span>}
        </span>
      </div>
      <div className={`
        flex ${isHorizontal ? 'flex-row' : 'flex-col'}
        ${isPlayer ? '' : 'scale-90'}
      `}>
        {sortedCards.map((card, idx) => (
          <div 
            key={card.id} 
            style={{ zIndex: idx }} 
            className={`
              ${idx > 0 ? overlap : ''}
              transition-transform
            `}
          >
            <Card
              card={card}
              position={position}
              onClick={onCardClick}
              disabled={playLock || !isCurrentPlayer || !legalCardIds.includes(card.id)}
              faceDown={!showCards && !isPlayer}
              small={!isPlayer}
              dealDelay={positionDelayBase[position] + (idx * 0.03)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// Trick Display - shows cards played in current trick with fly-in animations and win effects
const TrickDisplay = ({ trick, winner, showWinEffect }) => {
  // Play win sound when effect shows
  React.useEffect(() => {
    if (showWinEffect && winner) {
      SoundEffects.playWin();
    }
  }, [showWinEffect, winner]);

  // Define fly-in directions based on player position
  const getInitialPosition = (player) => {
    switch (player) {
      case 'SOUTH': return { y: 80, x: 0, scale: 0.5 };
      case 'NORTH': return { y: -80, x: 0, scale: 0.5 };
      case 'WEST': return { y: 0, x: -80, scale: 0.5 };
      case 'EAST': return { y: 0, x: 80, scale: 0.5 };
      default: return { y: 0, x: 0, scale: 0.5 };
    }
  };
  
  const positionStyles = {
    SOUTH: 'bottom-4 left-1/2 -translate-x-1/2',
    NORTH: 'top-4 left-1/2 -translate-x-1/2',
    WEST: 'left-4 top-1/2 -translate-y-1/2',
    EAST: 'right-4 top-1/2 -translate-y-1/2'
  };
  
  return (
    <div 
      className="relative w-72 h-48 rounded-2xl border-4 border-emerald-500/60"
      style={{
        background: 'radial-gradient(ellipse at center, #166534 0%, #14532d 50%, #052e16 100%)',
        boxShadow: 'inset 0 4px 20px rgba(0,0,0,0.4), 0 8px 32px rgba(0,0,0,0.3)'
      }}
    >
      {/* Felt texture */}
      <div className="absolute inset-0 rounded-xl opacity-30" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
      }} />
      
      {/* Win effect - golden glow pulse */}
      <AnimatePresence>
        {showWinEffect && winner && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.2 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 rounded-xl z-10 pointer-events-none"
            style={{
              background: 'radial-gradient(circle at center, rgba(251,191,36,0.4) 0%, rgba(251,191,36,0.1) 50%, transparent 70%)',
              boxShadow: 'inset 0 0 40px rgba(251,191,36,0.5)'
            }}
          >
            {/* Winner label */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            >
              <div className="bg-amber-500 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                {winner} wins!
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="absolute inset-0 flex items-center justify-center">
        {trick.length === 0 && !showWinEffect && (
          <span className="text-emerald-400/40 text-xl font-bold tracking-widest">PLAY AREA</span>
        )}
      </div>
      
      {trick.map(({ player, card }, index) => {
        const initial = getInitialPosition(player);
        const isWinningCard = showWinEffect && player === winner;
        
        return (
          <motion.div
            key={card.id}
            initial={{ 
              opacity: 0, 
              x: initial.x, 
              y: initial.y, 
              scale: initial.scale,
              rotate: player === 'WEST' ? -10 : player === 'EAST' ? 10 : 0
            }}
            animate={{ 
              opacity: 1, 
              x: 0, 
              y: 0, 
              scale: isWinningCard ? 1.1 : 1,
              rotate: 0
            }}
            transition={{ 
              type: 'spring', 
              damping: 12, 
              stiffness: 200,
              delay: index * 0.05
            }}
            className={`absolute ${positionStyles[player]} ${isWinningCard ? 'z-20' : ''}`}
            style={isWinningCard ? {
              filter: 'drop-shadow(0 0 12px rgba(251,191,36,0.8))'
            } : {}}
          >
            <Card card={card} small disabled dealDelay={0} />
          </motion.div>
        );
      })}
    </div>
  );
};

// Bid History Tooltip Component
const BidHistoryTooltip = ({ auction, show, position = 'bottom' }) => {
  if (!show || auction.length === 0) return null;
  
  const formatBid = (entry) => {
    if (entry.bid === SPECIAL_BIDS.PASS) return 'Pass';
    if (entry.bid === SPECIAL_BIDS.DOUBLE) return 'X';
    if (entry.bid === SPECIAL_BIDS.REDOUBLE) return 'XX';
    return `${entry.bid.level}${STRAIN_SYMBOLS[entry.bid.strain]}`;
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: position === 'bottom' ? -10 : 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className={`absolute ${position === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'} left-1/2 -translate-x-1/2 z-50`}
    >
      <div className="bg-slate-900 border-2 border-slate-600 rounded-xl p-4 shadow-2xl min-w-[200px]">
        <h4 className="text-amber-400 text-sm font-bold mb-3 flex items-center gap-2">
          <Info className="w-4 h-4" />
          Auction History
        </h4>
        <div className="grid grid-cols-4 gap-1 text-xs">
          <div className="text-center text-slate-500 font-bold pb-1 border-b border-slate-700">S</div>
          <div className="text-center text-slate-500 font-bold pb-1 border-b border-slate-700">W</div>
          <div className="text-center text-slate-500 font-bold pb-1 border-b border-slate-700">N</div>
          <div className="text-center text-slate-500 font-bold pb-1 border-b border-slate-700">E</div>
          {auction.map((entry, idx) => (
            <div 
              key={idx} 
              className="text-center text-white py-0.5"
              style={{ 
                color: entry.bid !== SPECIAL_BIDS.PASS && entry.bid !== SPECIAL_BIDS.DOUBLE && entry.bid !== SPECIAL_BIDS.REDOUBLE
                  ? STRAIN_COLORS[entry.bid.strain] 
                  : '#9ca3af'
              }}
            >
              {formatBid(entry)}
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Play Phase Tutorial Overlay
const PlayTutorial = ({ contract, dummy, declarer, onDismiss }) => {
  const isDummyVisible = dummy === 'NORTH';
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onDismiss}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-amber-500/30 rounded-2xl p-6 max-w-lg shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-amber-400" />
            </div>
            <h2 className="text-xl font-bold text-white">Play Phase Tips</h2>
          </div>
          <button 
            onClick={onDismiss}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          {/* Contract Info */}
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <h3 className="text-cyan-400 font-semibold mb-2">Your Contract</h3>
            <p className="text-white text-lg font-bold">
              {contract.level}
              {contract.strain === 'NOTRUMP' ? 'NT' : SUITS[contract.strain]?.symbol}
              {contract.doubled && ' Doubled'}
            </p>
            <p className="text-slate-400 text-sm mt-1">
              You need to win {6 + contract.level} tricks to make your contract.
            </p>
          </div>
          
          {/* Dummy Explanation */}
          <div className="bg-emerald-900/30 rounded-xl p-4 border border-emerald-700/50">
            <h3 className="text-emerald-400 font-semibold mb-2 flex items-center gap-2">
              <span className="text-lg">👁️</span> The Dummy Hand
            </h3>
            <p className="text-slate-300 text-sm">
              {declarer === 'SOUTH' ? (
                <>Your partner (North) is the dummy. Their cards are face-up and <span className="text-emerald-400 font-semibold">you control both hands</span>. Click cards from either hand when it's your turn.</>
              ) : (
                <>The dummy's cards ({dummy}) are visible to all players. The declarer ({declarer}) plays cards from both their hand and the dummy.</>
              )}
            </p>
          </div>
          
          {/* Lead Rules */}
          <div className="bg-blue-900/30 rounded-xl p-4 border border-blue-700/50">
            <h3 className="text-blue-400 font-semibold mb-2 flex items-center gap-2">
              <span className="text-lg">🎯</span> Lead Rules
            </h3>
            <ul className="text-slate-300 text-sm space-y-1">
              <li>• The player to the left of declarer leads the first trick</li>
              <li>• <span className="text-blue-400 font-semibold">You must follow suit</span> if you can</li>
              <li>• If you can't follow suit, you may play any card (including trump)</li>
              <li>• The winner of each trick leads the next one</li>
            </ul>
          </div>
          
          {/* Trump Info */}
          {contract.strain !== 'NOTRUMP' && (
            <div className="bg-rose-900/30 rounded-xl p-4 border border-rose-700/50">
              <h3 className="text-rose-400 font-semibold mb-2 flex items-center gap-2">
                <span className="text-lg">{SUITS[contract.strain]?.symbol}</span> Trump Suit
              </h3>
              <p className="text-slate-300 text-sm">
                <span style={{ color: STRAIN_COLORS[contract.strain] }} className="font-bold">
                  {contract.strain === 'HEARTS' || contract.strain === 'DIAMONDS' ? '♦♥' : '♠♣'} {contract.strain}
                </span> is trump. Trump cards beat any non-trump card, regardless of rank.
              </p>
            </div>
          )}
        </div>
        
        <Button
          onClick={onDismiss}
          className="w-full mt-6 bg-amber-600 hover:bg-amber-500 text-white font-bold"
        >
          Got it, let's play!
        </Button>
      </motion.div>
    </motion.div>
  );
};

// Sort Mode Toggle Button
const SortToggle = ({ sortMode, onToggle }) => (
  <div className="flex items-center gap-2 bg-slate-800/80 rounded-lg p-1">
    <button
      onClick={() => onToggle('suit')}
      className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${
        sortMode === 'suit' 
          ? 'bg-amber-500 text-white shadow-lg' 
          : 'text-slate-400 hover:text-white hover:bg-slate-700'
      }`}
    >
      By Suit
    </button>
    <button
      onClick={() => onToggle('rank')}
      className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${
        sortMode === 'rank' 
          ? 'bg-amber-500 text-white shadow-lg' 
          : 'text-slate-400 hover:text-white hover:bg-slate-700'
      }`}
    >
      By Rank
    </button>
  </div>
);

// Bidding Panel
const BiddingPanel = ({ auction, currentBidder, contract, onBid }) => {
  const [selectedLevel, setSelectedLevel] = useState(1);
  const [selectedStrain, setSelectedStrain] = useState('CLUBS');
  
  const isValidBid = () => {
    if (!contract) return true;
    if (selectedLevel > contract.level) return true;
    if (selectedLevel === contract.level) {
      return BID_STRAINS.indexOf(selectedStrain) > BID_STRAINS.indexOf(contract.strain);
    }
    return false;
  };
  
  const isPlayerTurn = currentBidder === 'SOUTH';
  
  return (
    <div className="bg-slate-900/95 rounded-xl p-5 border-2 border-slate-700 shadow-2xl backdrop-blur-sm">
      <h3 className="text-amber-400 text-base font-bold mb-4 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
        Bidding Phase
      </h3>
      
      {/* Auction history */}
      <div className="grid grid-cols-4 gap-1 text-sm mb-4 bg-slate-800 rounded-lg p-3">
        <div className="text-center text-slate-500 font-bold border-b border-slate-700 pb-1">S</div>
        <div className="text-center text-slate-500 font-bold border-b border-slate-700 pb-1">W</div>
        <div className="text-center text-slate-500 font-bold border-b border-slate-700 pb-1">N</div>
        <div className="text-center text-slate-500 font-bold border-b border-slate-700 pb-1">E</div>
        {auction.map((entry, idx) => (
          <div 
            key={idx} 
            className="text-center py-0.5 font-semibold"
            style={{
              color: entry.bid === SPECIAL_BIDS.PASS ? '#6b7280' :
                     entry.bid === SPECIAL_BIDS.DOUBLE ? '#ef4444' :
                     entry.bid === SPECIAL_BIDS.REDOUBLE ? '#8b5cf6' :
                     STRAIN_COLORS[entry.bid.strain]
            }}
          >
            {entry.bid === SPECIAL_BIDS.PASS ? 'Pass' :
             entry.bid === SPECIAL_BIDS.DOUBLE ? 'X' :
             entry.bid === SPECIAL_BIDS.REDOUBLE ? 'XX' :
             `${entry.bid.level}${STRAIN_SYMBOLS[entry.bid.strain]}`}
          </div>
        ))}
      </div>
      
      {isPlayerTurn && (
        <>
          {/* Level selection */}
          <div className="flex gap-1 mb-3">
            {BID_LEVELS.map(level => (
              <button
                key={level}
                onClick={() => setSelectedLevel(level)}
                className={`
                  flex-1 h-10 rounded-lg font-bold text-base transition-all
                  ${selectedLevel === level 
                    ? 'bg-amber-500 text-white shadow-lg scale-105' 
                    : 'bg-slate-700 text-gray-300 hover:bg-slate-600 hover:scale-102'}
                `}
              >
                {level}
              </button>
            ))}
          </div>
          
          {/* Strain selection */}
          <div className="flex gap-1 mb-4">
            {BID_STRAINS.map(strain => (
              <button
                key={strain}
                onClick={() => setSelectedStrain(strain)}
                className={`
                  flex-1 h-12 rounded-lg font-bold text-xl transition-all
                  ${selectedStrain === strain 
                    ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-slate-900 scale-105' 
                    : 'hover:bg-slate-600 hover:scale-102'}
                  bg-slate-700
                `}
                style={{ 
                  color: STRAIN_COLORS[strain],
                  textShadow: selectedStrain === strain ? '0 0 8px currentColor' : 'none'
                }}
              >
                {STRAIN_SYMBOLS[strain]}
              </button>
            ))}
          </div>
          
          {/* Bid buttons */}
          <div className="flex gap-2">
            <Button
              onClick={() => onBid({ level: selectedLevel, strain: selectedStrain })}
              disabled={!isValidBid()}
              className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 flex-1 font-bold text-base h-11 shadow-lg disabled:opacity-50"
            >
              Bid {selectedLevel}{STRAIN_SYMBOLS[selectedStrain]}
            </Button>
            <Button
              variant="outline"
              onClick={() => onBid(SPECIAL_BIDS.PASS)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white font-semibold"
            >
              Pass
            </Button>
          </div>
        </>
      )}
      
      {!isPlayerTurn && (
        <div className="text-center py-6">
          <div className="inline-flex items-center gap-2 text-slate-400">
            <div className="w-2 h-2 rounded-full bg-slate-500 animate-pulse"></div>
            <span className="font-semibold">Waiting for {currentBidder}...</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Score Panel
const ScorePanel = ({ score, tricksWon, contract, trickNumber, auction, gamePhase }) => {
  const [showBidHistory, setShowBidHistory] = useState(false);
  
  return (
    <div className="bg-slate-900/95 rounded-xl p-5 border-2 border-slate-700 shadow-2xl backdrop-blur-sm relative">
      <h3 className="text-cyan-400 text-base font-bold mb-4">Score</h3>
      
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="text-center bg-gradient-to-br from-blue-900/50 to-blue-950/50 rounded-xl p-3 border border-blue-700/30">
          <div className="text-3xl font-black text-white">{score.NS}</div>
          <div className="text-sm text-blue-300 font-semibold">N-S</div>
        </div>
        <div className="text-center bg-gradient-to-br from-rose-900/50 to-rose-950/50 rounded-xl p-3 border border-rose-700/30">
          <div className="text-3xl font-black text-white">{score.EW}</div>
          <div className="text-sm text-rose-300 font-semibold">E-W</div>
        </div>
      </div>
      
      {contract && (
        <div className="border-t-2 border-slate-700 pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400 font-semibold">Contract</span>
            {gamePhase === 'playing' && auction.length > 0 && (
              <div className="relative">
                <button
                  onMouseEnter={() => setShowBidHistory(true)}
                  onMouseLeave={() => setShowBidHistory(false)}
                  className="text-slate-400 hover:text-cyan-400 transition-colors p-1"
                  title="View bid history"
                >
                  <Info className="w-4 h-4" />
                </button>
                <AnimatePresence>
                  {showBidHistory && (
                    <BidHistoryTooltip auction={auction} show={showBidHistory} position="bottom" />
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
          <div 
            className="text-2xl font-black mb-1"
            style={{ color: contract.strain === 'NOTRUMP' ? '#10b981' : STRAIN_COLORS[contract.strain] }}
          >
            {contract.level}
            {contract.strain === 'NOTRUMP' ? 'NT' : SUITS[contract.strain]?.symbol}
            {contract.doubled && <span className="text-red-500 ml-1">X</span>}
            {contract.redoubled && <span className="text-purple-500 ml-1">XX</span>}
          </div>
          
          <div className="grid grid-cols-2 gap-2 mt-4">
            <div className="bg-slate-800/80 rounded-lg p-2 text-center">
              <div className="text-slate-400 text-xs font-semibold">N-S Tricks</div>
              <div className="text-white font-black text-xl">{tricksWon.NS}</div>
            </div>
            <div className="bg-slate-800/80 rounded-lg p-2 text-center">
              <div className="text-slate-400 text-xs font-semibold">E-W Tricks</div>
              <div className="text-white font-black text-xl">{tricksWon.EW}</div>
            </div>
          </div>
          
          <div className="mt-3 text-sm text-slate-500 font-semibold text-center bg-slate-800/50 rounded-lg py-2">
            Trick {trickNumber} of 13
          </div>
        </div>
      )}
    </div>
  );
};

// Bridge Coach Panel Component
const BridgeCoachPanel = ({ gamePhase, playerHand, auction, contract, currentTrick, trumpSuit, isLeading, tricksWon, coachEnabled, learningMode, onToggleCoach, onToggleLearning }) => {
  
  // Calculate coaching data
  const coachData = useMemo(() => {
    if (!playerHand || playerHand.length === 0) return null;
    
    const hcp = calculateHCP(playerHand);
    const { points: distPoints, shape } = calculateDistributionPoints(playerHand);
    const suits = analyzeSuits(playerHand);
    
    return { hcp, distPoints, totalPoints: hcp + distPoints, shape, suits };
  }, [playerHand]);
  
  // Get bidding or play suggestion
  const suggestion = useMemo(() => {
    if (!coachData) return null;
    
    if (gamePhase === 'bidding') {
      return getBiddingSuggestion(playerHand, auction.map(b => b.bid), false, 'none');
    } else if (gamePhase === 'playing') {
      return getPlaySuggestion({
        playerHand,
        currentTrick,
        trumpSuit,
        isLeading: currentTrick.length === 0,
        contract,
        tricksWon,
        tricksNeeded: contract ? contract.level + 6 : 7
      });
    }
    return null;
  }, [coachData, gamePhase, playerHand, auction, currentTrick, trumpSuit, contract, tricksWon]);
  
  const phase = gamePhase === 'bidding' ? 'bidding' : 'playing';
  const learning = getBridgeLearningExplanation(phase);
  const tips = getBridgeContextualTips(phase);
  
  if (!coachData) return null;
  
  return (
    <div className="bg-slate-900/90 rounded-xl border border-slate-700 overflow-hidden">
      {/* Toggle Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50">
        <div className="flex items-center gap-2">
          <Lightbulb className={`w-4 h-4 ${coachEnabled ? 'text-amber-400' : 'text-slate-500'}`} />
          <span className="text-xs font-semibold text-white">Bridge Coach</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onToggleLearning}
            className={`p-1 rounded ${learningMode ? 'bg-purple-500/30 text-purple-400' : 'text-slate-500 hover:text-slate-300'}`}
            title="Learning Mode"
          >
            <GraduationCap className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onToggleCoach}
            className={`p-1 rounded ${coachEnabled ? 'bg-amber-500/30 text-amber-400' : 'text-slate-500 hover:text-slate-300'}`}
            title="Coach Suggestions"
          >
            {coachEnabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      
      {/* Learning Mode Explanation */}
      {learningMode && (
        <div className="px-3 py-2 bg-purple-500/10 border-b border-purple-500/20">
          <div className="text-[10px] font-bold text-purple-400 mb-0.5">{learning.title}</div>
          <p className="text-[10px] text-purple-300/80 leading-relaxed">{learning.content}</p>
        </div>
      )}
      
      {coachEnabled && (
        <div className="p-3 space-y-3">
          {/* Hand Evaluation */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-slate-800/50 rounded-lg p-2 text-center">
              <div className="text-[10px] text-slate-500">HCP</div>
              <div className="text-lg font-bold text-amber-400">{coachData.hcp}</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-2 text-center">
              <div className="text-[10px] text-slate-500">Dist.</div>
              <div className="text-lg font-bold text-cyan-400">{coachData.distPoints}</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-2 text-center">
              <div className="text-[10px] text-slate-500">Total</div>
              <div className="text-lg font-bold text-green-400">{coachData.totalPoints}</div>
            </div>
          </div>
          
          {/* Shape */}
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Distribution</div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-white font-mono">{coachData.shape}</span>
              <span className="text-slate-400 text-[10px]">
                ({coachData.shape === '4-3-3-3' || coachData.shape === '4-4-3-2' || coachData.shape === '5-3-3-2' ? 'Balanced' : 'Unbalanced'})
              </span>
            </div>
          </div>
          
          {/* Suit Analysis */}
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Suits</div>
            <div className="grid grid-cols-4 gap-1">
              {['spades', 'hearts', 'diamonds', 'clubs'].map(suit => (
                <div key={suit} className="bg-slate-800/50 rounded p-1 text-center">
                  <span className={`text-sm ${suit === 'hearts' || suit === 'diamonds' ? 'text-red-500' : 'text-slate-300'}`}>
                    {suit === 'spades' ? '♠' : suit === 'hearts' ? '♥' : suit === 'diamonds' ? '♦' : '♣'}
                  </span>
                  <span className="text-[10px] text-white ml-1">{coachData.suits[suit]?.length || 0}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Suggestion */}
          {suggestion && (
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Suggestion</div>
              <div className="bg-amber-500/20 rounded-lg px-2 py-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-amber-400">
                    {gamePhase === 'bidding' ? suggestion.bid : (suggestion.card ? `${suggestion.card.display}${SUITS[suggestion.card.suit]?.symbol}` : '—')}
                  </span>
                  <span className="text-[10px] text-amber-300/70">{suggestion.confidence}%</span>
                </div>
                <p className="text-[10px] text-amber-300/80 mt-0.5">{suggestion.reason}</p>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Quick Tips */}
      {coachEnabled && (
        <div className="px-3 py-2 border-t border-slate-700/50">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Tip</div>
          <p className="text-[10px] text-slate-400 italic">
            {tips[Math.floor(Math.random() * tips.length)]}
          </p>
        </div>
      )}
    </div>
  );
};

// Main Bridge Game Component
const BridgeGame = () => {
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);
  const [coachEnabled, setCoachEnabled] = useState(true);
  const [learningMode, setLearningMode] = useState(false);
  
  const {
    gamePhase,
    hands,
    auction,
    currentBidder,
    contract,
    declarer,
    dummy,
    currentTrick,
    trickNumber,
    tricksWon,
    currentPlayer,
    score,
    message,
    playLock,
    sortMode,
    showTutorial,
    lastTrickWinner,
    showWinEffect,
    stepMode,
    waitingForContinue,
    lastAction,
    autoPlayDummy,
    startGame,
    startNextHand,
    makeBid,
    playCard,
    resetGame,
    setSortMode,
    dismissTutorial,
    toggleStepMode,
    toggleAutoPlayDummy,
    continueFromPause
  } = useBridgeStore();
  
  // Auto-start game on mount
  useEffect(() => {
    if (gamePhase === 'setup') {
      startGame();
    }
  }, [gamePhase, startGame]);
  
  // Show tutorial when entering play phase (use store's showTutorial as single source of truth)
  const shouldShowTutorial = gamePhase === 'playing' && showTutorial && contract;
  
  const handleDismissTutorial = () => {
    dismissTutorial();
  };
  
  const handleCardClick = (card) => {
    if (gamePhase === 'playing') {
      playCard(card);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-sm border-b-2 border-slate-700/50 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-black text-white">Bridge</h1>
              <p className="text-sm text-slate-400 font-medium">Classic Trick-Taking Card Game</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Sort Toggle - Only show during playing phase */}
            {gamePhase === 'playing' && (
              <SortToggle sortMode={sortMode} onToggle={setSortMode} />
            )}
            
            {/* Coach Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCoachEnabled(!coachEnabled)}
              className={`${coachEnabled ? 'text-amber-400' : 'text-slate-400 hover:text-white'} hover:bg-slate-800`}
              title="Toggle Coach"
            >
              <Lightbulb className="w-4 h-4" />
            </Button>
            
            {/* Step Mode Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleStepMode}
              className={`${stepMode ? 'bg-cyan-500/20 text-cyan-400 ring-1 ring-cyan-500/50' : 'text-slate-400 hover:text-white'} hover:bg-slate-800`}
              title={stepMode ? "Step Mode ON - Click to disable" : "Enable Step Mode (pause after each AI action)"}
              data-testid="step-mode-btn"
            >
              {stepMode ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {stepMode && <span className="ml-1 text-xs">STEP</span>}
            </Button>
            
            {/* Auto-Play Dummy Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleAutoPlayDummy}
              className={`${autoPlayDummy ? 'text-emerald-400' : 'text-slate-400 hover:text-white'} hover:bg-slate-800`}
              title={autoPlayDummy ? "Auto-Play Dummy ON" : "Auto-Play Dummy OFF (play obvious cards automatically)"}
              data-testid="auto-play-dummy-btn"
            >
              <Zap className="w-4 h-4" />
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHelp(true)}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <HelpCircle className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                resetGame();
                startGame();
              }}
              className="border-slate-600 text-white hover:bg-slate-800"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              New Game
            </Button>
          </div>
        </div>
      </div>
      
      {/* Game Area */}
      <div className="max-w-7xl mx-auto p-4">
        <div className="grid grid-cols-12 gap-4">
          {/* Left Panel - Bidding/Score */}
          <div className="col-span-3 space-y-4">
            {gamePhase === 'bidding' && (
              <BiddingPanel
                auction={auction}
                currentBidder={currentBidder}
                contract={contract}
                onBid={makeBid}
              />
            )}
            <ScorePanel
              score={score}
              tricksWon={tricksWon}
              contract={contract}
              trickNumber={trickNumber}
              auction={auction}
              gamePhase={gamePhase}
            />
            
            {/* Coach Panel */}
            {(coachEnabled || learningMode) && hands.SOUTH && hands.SOUTH.length > 0 && (
              <BridgeCoachPanel
                gamePhase={gamePhase}
                playerHand={hands.SOUTH}
                auction={auction}
                contract={contract}
                currentTrick={currentTrick}
                trumpSuit={contract?.strain !== 'NOTRUMP' ? contract?.strain : null}
                isLeading={currentTrick.length === 0}
                tricksWon={tricksWon}
                coachEnabled={coachEnabled}
                learningMode={learningMode}
                onToggleCoach={() => setCoachEnabled(!coachEnabled)}
                onToggleLearning={() => setLearningMode(!learningMode)}
              />
            )}
          </div>
          
          {/* Main Table */}
          <div className="col-span-9">
            <div 
              className="relative rounded-3xl border-4 border-emerald-600/50 p-6 min-h-[620px]"
              style={{
                background: 'radial-gradient(ellipse at center, #166534 0%, #14532d 40%, #052e16 100%)',
                boxShadow: '0 12px 48px rgba(0,0,0,0.4), inset 0 2px 0 rgba(255,255,255,0.05)'
              }}
            >
              {/* Felt texture overlay */}
              <div className="absolute inset-0 rounded-2xl opacity-20" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
              }} />
              
              {/* Message */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20">
                <motion.div 
                  key={message}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-slate-900/95 backdrop-blur-sm px-6 py-2.5 rounded-xl border-2 border-slate-600/50 shadow-xl"
                >
                  <p className="text-white text-sm font-semibold">{message}</p>
                </motion.div>
              </div>
              
              {/* North Hand */}
              <div className="absolute top-16 left-1/2 -translate-x-1/2">
                <Hand
                  cards={hands.NORTH}
                  position="NORTH"
                  isCurrentPlayer={currentPlayer === 'NORTH' || (dummy === 'NORTH' && declarer === 'SOUTH' && currentPlayer === 'NORTH')}
                  isDummy={dummy === 'NORTH'}
                  showCards={gamePhase === 'playing' && dummy === 'NORTH'}
                  onCardClick={handleCardClick}
                  currentTrick={currentTrick}
                  playLock={playLock}
                  sortMode={sortMode}
                />
              </div>
              
              {/* West Hand */}
              <div className="absolute left-6 top-1/2 -translate-y-1/2">
                <Hand
                  cards={hands.WEST}
                  position="WEST"
                  isCurrentPlayer={currentPlayer === 'WEST'}
                  isDummy={dummy === 'WEST'}
                  showCards={gamePhase === 'playing' && dummy === 'WEST'}
                  onCardClick={handleCardClick}
                  currentTrick={currentTrick}
                  playLock={playLock}
                  sortMode={sortMode}
                />
              </div>
              
              {/* East Hand */}
              <div className="absolute right-6 top-1/2 -translate-y-1/2">
                <Hand
                  cards={hands.EAST}
                  position="EAST"
                  isCurrentPlayer={currentPlayer === 'EAST'}
                  isDummy={dummy === 'EAST'}
                  showCards={gamePhase === 'playing' && dummy === 'EAST'}
                  onCardClick={handleCardClick}
                  currentTrick={currentTrick}
                  playLock={playLock}
                  sortMode={sortMode}
                />
              </div>
              
              {/* Center - Trick Display */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <TrickDisplay 
                  trick={currentTrick} 
                  winner={lastTrickWinner}
                  showWinEffect={showWinEffect}
                />
              </div>
              
              {/* South Hand (Player) */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
                <Hand
                  cards={hands.SOUTH}
                  position="SOUTH"
                  isCurrentPlayer={currentPlayer === 'SOUTH'}
                  isDummy={dummy === 'SOUTH'}
                  showCards={true}
                  onCardClick={handleCardClick}
                  currentTrick={currentTrick}
                  playLock={playLock}
                  sortMode={sortMode}
                />
              </div>
              
              {/* Partnership indicators */}
              <div className="absolute top-4 left-4">
                <div className="bg-blue-900/80 backdrop-blur-sm px-4 py-1.5 rounded-lg text-blue-200 text-sm font-bold border border-blue-600/50 shadow-lg">
                  N-S Partnership
                </div>
              </div>
              <div className="absolute top-4 right-4">
                <div className="bg-rose-900/80 backdrop-blur-sm px-4 py-1.5 rounded-lg text-rose-200 text-sm font-bold border border-rose-600/50 shadow-lg">
                  E-W Partnership
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Play Phase Tutorial */}
      <AnimatePresence>
        {shouldShowTutorial && contract && (
          <PlayTutorial
            contract={contract}
            dummy={dummy}
            declarer={declarer}
            onDismiss={handleDismissTutorial}
          />
        )}
      </AnimatePresence>
      
      {/* Scoring/Game Over Overlay */}
      <AnimatePresence>
        {gamePhase === 'scoring' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-amber-500/40 rounded-2xl p-8 max-w-md text-center shadow-2xl"
            >
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Trophy className="w-10 h-10 text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Hand Complete!</h2>
              <p className="text-gray-300 mb-6">{message}</p>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-blue-900/30 rounded-xl p-4 border border-blue-700/30">
                  <div className="text-3xl font-bold text-cyan-400">{score.NS}</div>
                  <div className="text-sm text-blue-300">North-South</div>
                  <div className="text-xs text-slate-400 mt-1">{tricksWon.NS} tricks</div>
                </div>
                <div className="bg-rose-900/30 rounded-xl p-4 border border-rose-700/30">
                  <div className="text-3xl font-bold text-amber-400">{score.EW}</div>
                  <div className="text-sm text-rose-300">East-West</div>
                  <div className="text-xs text-slate-400 mt-1">{tricksWon.EW} tricks</div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => startNextHand()}
                  className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 flex-1 font-bold shadow-lg"
                  data-testid="next-hand-btn"
                >
                  Next Hand
                </Button>
                <Button
                  onClick={() => {
                    resetGame();
                    startGame();
                  }}
                  variant="outline"
                  className="border-slate-500 text-slate-300 hover:bg-slate-700"
                  data-testid="new-game-btn"
                >
                  New Game
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Step Mode Continue Overlay */}
      <AnimatePresence>
        {waitingForContinue && lastAction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40"
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              className="bg-gradient-to-r from-slate-900 to-slate-800 border-2 border-cyan-500/50 rounded-2xl px-6 py-4 shadow-2xl shadow-cyan-500/20 relative"
            >
              {/* Dismiss button - turns off Step Mode */}
              <button
                onClick={() => {
                  toggleStepMode(); // Turn off step mode
                  continueFromPause(); // Continue the game
                }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-slate-700 hover:bg-slate-600 rounded-full flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                title="Turn off Step Mode and continue"
              >
                <X className="w-4 h-4" />
              </button>
              
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">
                    {lastAction.player} {lastAction.type === 'bid' ? 'bids' : 'plays'}
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {lastAction.detail}
                  </div>
                </div>
                <Button
                  onClick={continueFromPause}
                  className="bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 font-bold px-6"
                  data-testid="continue-btn"
                >
                  Continue →
                </Button>
              </div>
              <div className="text-xs text-slate-500 text-center mt-2">
                Step Mode pauses after each AI action • Click ✕ to disable
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Help Modal */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowHelp(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-slate-600 rounded-2xl p-6 max-w-lg max-h-[80vh] overflow-y-auto shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">How to Play Bridge</h2>
                <button onClick={() => setShowHelp(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4 text-gray-300 text-sm">
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <h3 className="font-semibold text-amber-400 mb-2">Overview</h3>
                  <p>Bridge is a trick-taking game for 4 players in two partnerships (North-South vs East-West). You play as South with an AI partner (North) against AI opponents.</p>
                </div>
                
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <h3 className="font-semibold text-amber-400 mb-2">Bidding Phase</h3>
                  <p>Players bid to determine the contract. A bid has a level (1-7) and a strain (♣ ♦ ♥ ♠ or NT). Higher bids outrank lower ones. The final bid becomes the contract.</p>
                </div>
                
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <h3 className="font-semibold text-amber-400 mb-2">Play Phase</h3>
                  <p>The declarer's partner (dummy) lays their cards face up. The declarer plays both hands. Win tricks by playing the highest card of the led suit, or by trumping.</p>
                </div>
                
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <h3 className="font-semibold text-amber-400 mb-2">Scoring</h3>
                  <p>Make your contract to score points. Fail and the opponents score penalties. Bid higher for more points but greater risk!</p>
                </div>
                
                <div className="bg-cyan-900/30 rounded-xl p-4 border border-cyan-700/30">
                  <h3 className="font-semibold text-cyan-400 mb-2">Card Rankings</h3>
                  <p className="font-mono">A K Q J 10 9 8 7 6 5 4 3 2 (Ace highest)</p>
                </div>
                
                <div className="bg-cyan-900/30 rounded-xl p-4 border border-cyan-700/30">
                  <h3 className="font-semibold text-cyan-400 mb-2">Suit Rankings (for bidding)</h3>
                  <p className="font-mono">NT {'>'} ♠ {'>'} ♥ {'>'} ♦ {'>'} ♣</p>
                </div>
              </div>
              
              <Button
                onClick={() => setShowHelp(false)}
                className="mt-6 w-full bg-slate-700 hover:bg-slate-600"
              >
                Got it!
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Mode Selector Component
const BridgeModeSelector = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(null);
  
  useEffect(() => {
    const roomId = searchParams.get('room');
    if (roomId) {
      setMode('multi');
    }
  }, [searchParams]);

  if (mode === 'single') {
    return <BridgeGame />;
  }

  if (mode === 'multi') {
    return <BridgeMultiplayer />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="absolute top-4 left-4 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-4xl font-bold text-white mb-2">Bridge</h1>
          <p className="text-gray-400">Classic Trick-Taking Card Game</p>
        </div>

        {/* Mode Selection */}
        <div className="space-y-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setMode('single')}
            className="w-full bg-slate-900/90 backdrop-blur-sm rounded-xl border border-slate-700 p-6 text-left hover:border-emerald-500/50 transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-emerald-600/20 flex items-center justify-center group-hover:bg-emerald-600/30 transition-colors">
                <Bot className="w-7 h-7 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-1">Play vs AI</h2>
                <p className="text-sm text-gray-400">Practice with computer partners</p>
              </div>
            </div>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setMode('multi')}
            className="w-full bg-slate-900/90 backdrop-blur-sm rounded-xl border border-slate-700 p-6 text-left hover:border-cyan-500/50 transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-cyan-600/20 flex items-center justify-center group-hover:bg-cyan-600/30 transition-colors">
                <Wifi className="w-7 h-7 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-1">Multiplayer</h2>
                <p className="text-sm text-gray-400">Play with friends online</p>
              </div>
            </div>
          </motion.button>
        </div>

        {/* Game Info */}
        <div className="mt-8 bg-slate-900/50 rounded-xl border border-slate-700/50 p-4">
          <h3 className="text-sm font-semibold text-amber-400 mb-2">How to Play</h3>
          <ul className="text-sm text-gray-400 space-y-1">
            <li>• 4 players in partnerships (N-S vs E-W)</li>
            <li>• Bid to set the contract level and trump suit</li>
            <li>• Win tricks to make your contract</li>
            <li>• Score points for contracts made</li>
          </ul>
        </div>
      </motion.div>
    </div>
  );
};

export default BridgeModeSelector;
